// src/oura/board/OpenWorkViews.tsx
//
// The Tracker's open work parts: the month calendar with the picked day's
// list, the day strip's buttons, and a row of the open work list. Each row
// links to the deliverable on its client's Delivery tab.

import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { ChevronLeftIcon, ChevronRightIcon } from "lucide-react";
import { Tag, TYPE } from "@oura/ui";
import { dayLabel } from "@service/aeo/delivery";
import { plural } from "@oura/docs";
import { cn } from "../lib/cn";
import { deliveryHref, WORK_COLS } from "./links";
import { GROUP_WORD, monthWeeks, type WorkRow } from "./model";

const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

/** The month as a calendar on the left, the picked day's work on the right. */
export function CalendarView({
    month,
    today,
    day,
    rowsOn,
    onMonth,
    onToday,
    onDay,
    filters,
}: {
    month: string;
    today: string;
    day: string;
    rowsOn: (ymd: string) => WorkRow[];
    onMonth: (n: number) => void;
    onToday: () => void;
    onDay: (ymd: string) => void;
    filters: ReactNode;
}) {
    const weeks = monthWeeks(month);
    const picked = rowsOn(day);
    const [y, m] = month.split("-").map(Number);
    return (
        <div className="grid grid-cols-1 lg:grid-cols-[minmax(320px,400px)_minmax(0,1fr)]">
            <div className="border-b border-gray-100 p-4 lg:border-b-0 lg:border-r">
                <div className="mb-3 flex items-center justify-between gap-2">
                    <span className={cn(TYPE.tt, "text-gray-900")}>
                        {MONTHS[m - 1]} {y}
                    </span>
                    <span className="flex items-center gap-1">
                        <button type="button" onClick={onToday} className={cn(TYPE.tc, "rounded-full px-2.5 py-1 font-medium text-blue-700 hover:bg-blue-50")}>
                            Today
                        </button>
                        <button type="button" aria-label="Previous month" onClick={() => onMonth(-1)} className="rounded-full p-1.5 text-gray-600 hover:bg-gray-100">
                            <ChevronLeftIcon className="h-4 w-4" />
                        </button>
                        <button type="button" aria-label="Next month" onClick={() => onMonth(1)} className="rounded-full p-1.5 text-gray-600 hover:bg-gray-100">
                            <ChevronRightIcon className="h-4 w-4" />
                        </button>
                    </span>
                </div>
                <div className="grid grid-cols-7 gap-1">
                    {WEEKDAYS.map((w) => (
                        <span key={w} className={cn(TYPE.tm, "pb-1 text-center font-medium text-gray-400")}>
                            {w}
                        </span>
                    ))}
                    {weeks.flat().map((d) => {
                        const rows = rowsOn(d);
                        const late = rows.filter((r) => r.when === "overdue").length;
                        const done = rows.filter((r) => r.item.group === "done").length;
                        const openN = rows.length - done - late;
                        const inMonth = d.slice(0, 7) === month;
                        const isDay = d === day;
                        return (
                            <button
                                key={d}
                                type="button"
                                aria-pressed={isDay}
                                aria-label={`${dayLabel(d)}: ${plural(rows.length, "deliverable")}`}
                                onClick={() => onDay(d)}
                                className={cn(
                                    "flex aspect-square min-h-[44px] flex-col items-center justify-between rounded-lg p-1 transition focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-600",
                                    isDay ? "bg-gray-900 text-white" : rows.length > 0 ? "bg-gray-50 hover:bg-gray-100" : "hover:bg-gray-50",
                                    d === today && !isDay && "ring-2 ring-inset ring-blue-600",
                                )}
                            >
                                <span className={cn(TYPE.tc, "font-medium tabular-nums", !isDay && (inMonth ? "text-gray-900" : "text-gray-300"))}>{Number(d.slice(8))}</span>
                                {rows.length > 0 ? (
                                    <span className="flex items-center gap-0.5">
                                        {late > 0 && <span className="h-1.5 w-1.5 rounded-full bg-red-500" />}
                                        {openN > 0 && <span className="h-1.5 w-1.5 rounded-full bg-blue-600" />}
                                        {done > 0 && <span className={cn("h-1.5 w-1.5 rounded-full", isDay ? "bg-white/70" : "bg-gray-400")} />}
                                        <span className={cn("ml-0.5 text-[10px] font-medium tabular-nums", isDay ? "text-white/80" : "text-gray-500")}>{rows.length}</span>
                                    </span>
                                ) : (
                                    <span className="h-3" />
                                )}
                            </button>
                        );
                    })}
                </div>
                <p className={cn(TYPE.tm, "m-0 mt-3 flex flex-wrap gap-x-3 gap-y-1 text-gray-500")}>
                    <span className="inline-flex items-center gap-1">
                        <span className="h-1.5 w-1.5 rounded-full bg-red-500" />
                        Past its date
                    </span>
                    <span className="inline-flex items-center gap-1">
                        <span className="h-1.5 w-1.5 rounded-full bg-blue-600" />
                        Open
                    </span>
                    <span className="inline-flex items-center gap-1">
                        <span className="h-1.5 w-1.5 rounded-full bg-gray-400" />
                        Done
                    </span>
                </p>
                <div className="mt-4 flex flex-wrap gap-2">{filters}</div>
            </div>
            <div className="min-w-0">
                <div className="flex items-center gap-2 border-b border-gray-100 px-4 py-3">
                    <span className={cn(TYPE.tb, "font-semibold text-gray-900")}>{dayHead(day, today)}</span>
                    <span className={cn(TYPE.tc, "tabular-nums text-gray-400")}>{picked.length}</span>
                </div>
                {picked.length === 0 ? (
                    <p className={cn(TYPE.tb, "m-0 py-10 text-center text-gray-500")}>Nothing is due on {dayLabel(day)}.</p>
                ) : (
                    <ul className="list-plain m-0 divide-y divide-gray-100 p-0">
                        {picked.map((r) => (
                            <DayRow key={`${r.client.id}:${r.item.key}`} row={r} />
                        ))}
                    </ul>
                )}
            </div>
        </div>
    );
}

/** "Today, Wed 21 Oct" or "Fri 23 Oct". */
function dayHead(ymd: string, today: string): string {
    return ymd === today ? `Today, ${dayLabel(ymd)}` : dayLabel(ymd);
}

/** One deliverable in the calendar's day list: the client, the work, who has it, where it stands. */
function DayRow({ row: r }: { row: WorkRow }) {
    const g = GROUP_WORD[r.item.group];
    const late = r.when === "overdue";
    return (
        <li>
            <Link to={deliveryHref(r.client.id, r.item.key)} className="group flex items-start gap-3 px-4 py-3 no-underline hover:bg-gray-50">
                <span className={cn("mt-1.5 h-2 w-2 shrink-0 rounded-full", late ? "bg-red-500" : r.item.group === "done" ? "bg-gray-400" : "bg-blue-600")} />
                <span className="min-w-0 flex-1">
                    <span className={cn(TYPE.tc, "block text-gray-500")}>{r.client.name}</span>
                    <span className={cn(TYPE.tb, "block truncate font-medium text-gray-900 group-hover:text-blue-700")}>
                        {r.item.label}
                        <span className="ml-1.5 font-normal tabular-nums text-gray-400">
                            {r.item.code} · M{r.item.month}
                        </span>
                    </span>
                    {r.item.next && <span className={cn(TYPE.tc, "block truncate text-gray-500")}>{r.item.next}</span>}
                </span>
                <span className="flex shrink-0 flex-col items-end gap-1">
                    <Tag tone={late ? "red" : g.tone}>{late ? "Past its date" : g.label}</Tag>
                    <span className={cn(TYPE.tm, "text-gray-500")}>{r.item.owner}</span>
                </span>
            </Link>
        </li>
    );
}

export function DayButton({
    active,
    onClick,
    top,
    big,
    bottom,
    alert,
    muted,
    today,
}: {
    active: boolean;
    onClick: () => void;
    top: string;
    big: string;
    bottom: string;
    alert?: boolean;
    muted?: boolean;
    today?: boolean;
}) {
    return (
        <button
            type="button"
            aria-pressed={active}
            onClick={onClick}
            className={cn(
                "flex w-[68px] shrink-0 flex-col items-center rounded-xl px-2 py-2 ring-1 ring-inset transition focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-600",
                active ? "bg-gray-900 text-white ring-gray-900" : alert ? "bg-[#FDECEC] text-[#C5221F] ring-red-200 hover:bg-red-100" : "bg-white text-gray-900 ring-gray-200 hover:bg-gray-50",
                today && !active && "ring-2 ring-blue-600",
            )}
        >
            <span className={cn(TYPE.tm, "font-medium", active ? "text-white/80" : alert ? "" : today ? "text-blue-700" : "text-gray-500")}>{top}</span>
            <span className={cn("text-[20px] font-semibold leading-7 tabular-nums", !active && muted && "text-gray-300")}>{big}</span>
            <span className={cn(TYPE.tm, active ? "text-white/70" : alert ? "" : "text-gray-400")}>{bottom}</span>
        </button>
    );
}

export function WorkRowView({ row: r }: { row: WorkRow }) {
    const g = GROUP_WORD[r.item.group];
    return (
        <li>
            <Link
                to={deliveryHref(r.client.id, r.item.key)}
                className={cn(WORK_COLS, "group flex flex-wrap items-start gap-x-3 gap-y-1 px-4 py-3 no-underline hover:bg-gray-50 lg:items-center")}
            >
                <span className={cn(TYPE.tc, "w-24 shrink-0 whitespace-nowrap tabular-nums", r.when === "overdue" ? "font-medium text-red-600" : "text-gray-700")}>{dayLabel(r.item.due)}</span>
                <span className={cn(TYPE.tc, "min-w-0 flex-1 truncate text-gray-500 lg:text-gray-900")}>{r.client.name}</span>
                <span className="min-w-0 basis-full lg:basis-auto">
                    <span className={cn(TYPE.tb, "block truncate font-medium text-gray-900 group-hover:text-blue-700")}>
                        {r.item.label}
                        <span className="ml-1.5 font-normal tabular-nums text-gray-400">
                            {r.item.code} · M{r.item.month}
                        </span>
                    </span>
                    {r.item.next && <span className={cn(TYPE.tc, "block truncate text-gray-500")}>{r.item.next}</span>}
                </span>
                <span className={cn(TYPE.tc, "truncate text-gray-700")}>{r.item.owner}</span>
                <span>
                    <Tag tone={g.tone}>{g.label}</Tag>
                </span>
            </Link>
        </li>
    );
}
